import { Service, computed, inject, signal } from '@angular/core';
import {
  SAPHIR_MODULE_EXPORT,
  SaphirModuleDefinition,
  SaphirModuleManifest,
  isSaphirModuleDefinition,
} from '@saphir/module-contract';

import { MODULE_PACKAGES } from '../core/modules/module-packages';
import {
  MODULES_CONFIG_LOADER,
  MODULES_CONFIG_URL,
  ModuleAudience,
  ModuleConfigEntry,
  parseModulesConfig,
} from '../core/modules/modules-config';
import { ModuleTranslationsService } from './module-translations.service';

/**
 * Outcome of resolving one configured module.
 *
 * Everything but `ready` is a deployment fault rather than a user-facing state,
 * which is why each has its own value: the dashboard shows the same disabled
 * card for all of them, but support needs to tell them apart.
 */
export type ModuleStatus =
  /** Definition imported and valid — the module can be opened. */
  | 'ready'
  /** Configured, but no package is registered under that id. */
  | 'missing'
  /** Package loaded, but what it exports is not a module definition. */
  | 'invalid'
  /** The import itself threw — a broken chunk, or an offline network. */
  | 'failed';

export interface RegisteredModule {
  readonly id: string;
  readonly order: number;
  readonly status: ModuleStatus;
  readonly category?: string;
  /**
   * Who the module is offered to, from the config. See `ModuleConfigEntry`.
   *
   * Optional so a caller constructing one of these by hand — a spec, a stub —
   * describes only what it cares about; absent reads as `everyone`, which is
   * the same default the config parser applies.
   */
  readonly audience?: ModuleAudience;
  /**
   * The UMC module `Code` this module is licensed under, from the config.
   * Absent means the id doubles as the code — see `ModuleConfigEntry`.
   */
  readonly licenseCode?: string;
  /** Present exactly when `status` is `ready`. */
  readonly manifest?: SaphirModuleManifest;
}

/**
 * Which modules this installation offers, and their definitions.
 *
 * Populated once during application start (see `provideModuleRegistry`) by
 * combining two sources: `modules.config.json` says which modules to offer,
 * `MODULE_PACKAGES` says which ones are actually installed.
 *
 * Resolving a module means importing its package entry point — a few hundred
 * bytes holding the manifest — not its screens. Those are behind
 * `loadRoutes()` and stay unfetched until the module is opened.
 */
@Service()
export class ModuleRegistryService {
  private readonly loadConfig = inject(MODULES_CONFIG_LOADER);
  private readonly packages = inject(MODULE_PACKAGES);
  private readonly translations = inject(ModuleTranslationsService);

  private readonly definitions = new Map<string, SaphirModuleDefinition>();
  private readonly registered = signal<readonly RegisteredModule[]>([]);
  private readonly registryProblems = signal<readonly string[]>([]);

  /**
   * Whether the person looking is platform staff.
   *
   * Pushed in by `CompanyContextService` rather than injected from it: that
   * service already depends on this one (it reads the registry to translate
   * permission verbs), so reaching back would close a cycle. It is also not
   * known at `load()` time — the registry is built during application start,
   * the UMC context arrives afterwards — which is why the audience filter is a
   * computed over a signal rather than something applied while resolving.
   */
  private readonly viewerIsSuperAdmin = signal(false);

  setViewerIsSuperAdmin(isSuperAdmin: boolean): void {
    this.viewerIsSuperAdmin.set(isSuperAdmin);
  }

  /**
   * Every enabled module the CURRENT viewer is offered, in dashboard order,
   * whether or not it resolved.
   *
   * Audience is applied here rather than at each call site so the dashboard,
   * the sidebar, the command palette and the routes cannot disagree about what
   * exists — a module hidden from the dashboard but reachable by URL is not
   * hidden.
   */
  readonly modules = computed(() =>
    this.registered().filter(
      // Absent audience is 'everyone': only an explicit restriction hides a
      // module, so a module that predates the field keeps being offered.
      (module) => module.audience !== 'superAdmin' || this.viewerIsSuperAdmin(),
    ),
  );

  /**
   * Every enabled module, audience ignored.
   *
   * For the callers that ask what this INSTALLATION offers rather than what the
   * viewer may see — permission-verb translation needs the mapping of a module
   * it is not showing anybody.
   */
  readonly allModules = this.registered.asReadonly();

  /** The subset that can actually be opened. */
  readonly availableModules = computed(() =>
    this.modules().filter((module) => module.status === 'ready'),
  );

  /** What went wrong while reading the configuration, for a support screen. */
  readonly problems = this.registryProblems.asReadonly();

  /** The definition of a resolved module; `undefined` for every other status. */
  definitionOf(id: string): SaphirModuleDefinition | undefined {
    return this.definitions.get(id);
  }

  /**
   * Reads the configuration and resolves every enabled module.
   *
   * Never rejects. A missing config file, a malformed entry or a broken package
   * costs the modules it affects, nothing more — the shell still starts, and
   * the dashboard reports what is unavailable.
   */
  async load(): Promise<void> {
    let raw: unknown;

    try {
      raw = await this.loadConfig();
    } catch (error) {
      this.publish([], [`Could not read ${MODULES_CONFIG_URL}: ${describeError(error)}.`]);
      return;
    }

    const { entries, problems } = parseModulesConfig(raw);
    const resolutions = await Promise.all(
      entries.filter((entry) => entry.enabled).map((entry) => this.resolve(entry)),
    );

    this.publish(
      resolutions.map((resolution) => resolution.module),
      [...problems, ...resolutions.flatMap((resolution) => resolution.problems)],
    );
  }

  private async resolve(entry: ModuleConfigEntry): Promise<ModuleResolution> {
    const importPackage = this.packages[entry.id];

    if (!importPackage) {
      return rejected(entry, 'missing', `no package is registered for module "${entry.id}"`);
    }

    try {
      const namespace = await importPackage();
      const definition = (namespace as Record<string, unknown> | null)?.[SAPHIR_MODULE_EXPORT];

      if (!isSaphirModuleDefinition(definition)) {
        return rejected(
          entry,
          'invalid',
          `the package of module "${entry.id}" does not export a valid "${SAPHIR_MODULE_EXPORT}"` +
            ' — it was probably built against a different contract version',
        );
      }

      if (definition.manifest.id !== entry.id) {
        return rejected(
          entry,
          'invalid',
          `the package registered as "${entry.id}" declares the id "${definition.manifest.id}"`,
        );
      }

      this.definitions.set(entry.id, definition);

      return {
        module: {
          id: entry.id,
          order: entry.order,
          status: 'ready',
          category: entry.category ?? definition.manifest.category,
          licenseCode: entry.licenseCode,
          audience: entry.audience,
          manifest: definition.manifest,
        },
        // Merged here rather than when the module is opened: the dashboard
        // renders the manifest's `titleKey`, so the dictionary has to be in
        // place before the module has been opened even once. A module whose
        // translations fail still counts as ready — untranslated keys are ugly,
        // not a reason to withhold a working module.
        problems: await this.translations.load(definition),
      };
    } catch (error) {
      return rejected(
        entry,
        'failed',
        `module "${entry.id}" failed to load: ${describeError(error)}`,
      );
    }
  }

  private publish(modules: readonly RegisteredModule[], problems: readonly string[]): void {
    this.registered.set(modules);
    this.registryProblems.set(problems);

    // Logged in every environment on purpose: this is the information support
    // needs when a customer reports a missing tile, and it is never noise —
    // a healthy installation produces none of it.
    for (const problem of problems) {
      console.error(`[modules] ${problem}`);
    }
  }
}

interface ModuleResolution {
  readonly module: RegisteredModule;
  readonly problems: readonly string[];
}

function rejected(
  entry: ModuleConfigEntry,
  status: Exclude<ModuleStatus, 'ready'>,
  problem: string,
): ModuleResolution {
  return {
    module: {
      id: entry.id,
      order: entry.order,
      status,
      category: entry.category,
      licenseCode: entry.licenseCode,
      audience: entry.audience,
    },
    problems: [`Module "${entry.id}" is unavailable: ${problem}.`],
  };
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
